// src/hooks/useInitializeApi.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useZustand from 'hooks/useZustand';
import { decrementRequesting, incrementRequesting } from 'services/requestTrackerService';
import axiosService from 'services/axiosService';
import { clone, isMongoObjectId } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';
import appService from 'services/appService';
import { useDocumentContext } from 'components/core/DocumentProvider';
import useUrl from 'hooks/useUrl';
import translateX from 'utils/translateX';
import { moduleStore } from 'stores/module';
import {
  getQuery,
  mergeUrlParamsQuery,
  removeUrlParams,
  updateUrl,
  getUrl,
  getDomain,
  getUrlEndpoint,
  getParams,
} from 'utils/urlUtils';
import { markRequestAsFailed, markJustSaved, markJustRefreshed } from 'hooks/useApi';
import routeService from 'services/routeService';
import {
  parseImportToken,
  getImportTokenFromUrl,
  QUERY_PARAM_NAME,
} from 'utils/crossProfileImport';

// Tracker to prevent duplicate abort executions
let abortExecutionTracker = null;

// Tracker to suppress notifications for intentionally deleted documents
// Used when a document is deleted via UI action (e.g., RemoveSubscriptionModal)
// to prevent the "document not found" notification from appearing
let intentionalDeletionTimestamp = null;

// Export function to clear abort tracker (called when page transition completes)
export const clearAbortTracker = () => {
  abortExecutionTracker = null;
};

// Export function to mark that a document was intentionally deleted
// This suppresses the "document not found" notification for 10 seconds
export const markIntentionalDeletion = () => {
  intentionalDeletionTimestamp = Date.now();
};

// Export function to check if deletion was intentional (within 10 seconds)
export const wasIntentionalDeletion = () => {
  if (!intentionalDeletionTimestamp) return false;
  const elapsed = Date.now() - intentionalDeletionTimestamp;
  // Clear the flag if more than 10 seconds have passed
  if (elapsed > 10000) {
    intentionalDeletionTimestamp = null;
    return false;
  }
  return true;
};

// Export function to clear intentional deletion flag
export const clearIntentionalDeletion = () => {
  intentionalDeletionTimestamp = null;
};

export const abortUnauthorizedDocument = (navigate, options = {}) => {
  const {
    reason = 'documentNotFound', // 'documentNotFound' | 'unauthorizedAccess'
  } = options;

  // Prevent duplicate execution within 5 seconds (enough time for page transition)
  const now = Date.now();
  if (abortExecutionTracker && now - abortExecutionTracker < 5000) {
    return null;
  }

  abortExecutionTracker = now;

  const setPageDataStore = useZustand('module', 'setPageData', false);
  const setPageBackStore = useZustand('module', 'setPageBack', false);
  const storedCurrentPage = useZustand('module', 'currentPage', false);

  // Check if this was an intentional deletion - if so, skip notification
  // and clear the flag to prevent affecting future calls
  if (wasIntentionalDeletion()) {
    clearIntentionalDeletion();
    // Still clear page data and navigate, but skip the notification
    setPageDataStore({ data: null, page: storedCurrentPage?.name });
    return null;
  }

  // Show warning notification based on reason
  const isUnauthorized = reason === 'unauthorizedAccess';

  appService.notification({
    flag: 'warning',
    title: translateX('document_not_found_notification_title'),
    description: translateX('document_not_found_notification_description'),
  });

  // Clear current page data
  setPageDataStore({ data: null, page: storedCurrentPage?.name });

  // Navigate based on reason:
  // - documentNotFound: go back to previous page (setPageBack)
  // - unauthorizedAccess: go to home page
  setTimeout(() => {
    if (isUnauthorized) {
      navigate('/app/dashboard/overview', { replace: true });
    } else {
      // Use setPageBack to navigate - it handles:
      // 1. Zustand navigationHistory (if available)
      // 2. localStorage 'backTo' data (after F5)
      // 3. Fallback to module's 'init' page with current params
      setPageBackStore({ navigate });
    }
  }, 500); // Small delay to show the notification first

  return null;
};

// *******************************************************************************
// MAIN HOOK
// *******************************************************************************
const useApiInitializer = (props = {}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  const { setViewMode: setMode } = useDocumentContext();

  const setPageDataStore = useZustand('module', 'setPageData');
  const setReadyStore = useZustand('module', 'setReady');
  const setLoadingAreaStore = useZustand('boot', 'setLoadingArea');
  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedModule = useZustand('module', 'config');
  const storedFormDebug = useZustand('module', 'formDebug'); // Enable reactivity to capture form state
  const storedUser = useZustand('auth', 'user');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  // const params = useParams();
  const { api: urlApi, params } = useUrl();

  // Create a ref to store all reactive hook values
  // This ensures callbacks always have access to the latest values
  const hookRef = useRef({});

  // Update hookRef with latest values on every render
  hookRef.current = {
    url: props.url ?? urlApi ?? null,
    params: props.params ?? params ?? storedCurrentPage?.api?.params ?? {},
    query: props.query ?? storedCurrentPage?.api?.query ?? getQuery?.(),
    condition: props.condition ?? {},
    headers: props.headers ?? {},
    decorator: props.decorator ?? null,
    fetchWithoutSubscription:
      props.fetchWithoutSubscription ?? storedCurrentPage?.api?.fetchWithoutSubscription ?? false,
    noFetchDataStore: props.noFetchDataStore ?? false,
    removeSubscriptionIdFromHeader:
      props.removeSubscriptionIdFromHeader ??
      storedCurrentPage?.api?.removeSubscriptionIdFromHeader ??
      false,
    ignoreLoadingHide: props.ignoreLoadingHide ?? false,
    ignoreLoadingShow: props.ignoreLoadingShow ?? false,
    // Consider the routing paramKey (e.g. 'subscriptionId') when resolving _id from URL params.
    // This ensures refresh()/get() without args preserves the correct document id on pages
    // whose route uses a custom paramKey instead of the generic '_id'.
    _id:
      props._id ??
      params?._id ??
      (storedCurrentPage?.routing?.paramKey
        ? params?.[storedCurrentPage.routing.paramKey]
        : null) ??
      null,
    returnAsObject: props.returnAsObject ?? !!storedCurrentPage?.api?.returnAsObject,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [documentId, setDocumentId] = useState(null);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const done = useCallback(
    (hideLoading = true) => {
      if (!hideLoading) {
        return;
      }
      setReadyStore(true);
      setLoading(false);
    },
    [setReadyStore, setLoading]
  );

  const handleResult = (docs, result, noFetchDataStore, mergeWithExisting = false) => {
    setDocumentId(docs?._id || null);

    const currentPage = storedCurrentPage;
    const decorator = hookRef.current.decorator || currentPage?.decorators?.response;
    const clonedDocs = clone(docs);

    const decorated = Array.isArray(clonedDocs)
      ? clonedDocs.map(doc => decorator?.(doc) || doc)
      : decorator?.(clonedDocs) || clonedDocs;

    if (!noFetchDataStore) {
      // Read currentPage DIRECTLY from the Zustand store to guarantee the latest value
      const freshCurrentPage = moduleStore().currentPage;
      const targetPageName = freshCurrentPage?.name || currentPage?.name;
      const existingData = freshCurrentPage?.data;

      // When mergeWithExisting is true (for PUT/POST), merge new data with existing data
      // This preserves fields that weren't returned by the API (like computed fields, relations, etc.)
      const finalData =
        mergeWithExisting && existingData && !Array.isArray(decorated)
          ? { ...existingData, ...decorated }
          : decorated;

      setData(finalData);
      setSummary(result?.summary);

      const payload = {
        data: finalData,
        page: targetPageName,
        force: mergeWithExisting, // Force persist on PUT/POST to ensure data is saved
      };

      setPageDataStore(payload);
    }

    return decorated;
  };

  const showSuccessMessage = (message, show) => {
    if (show) {
      setTimeout(() => appService.message({ flag: 's', text: message, key: 'saveContext' }), 0);
    }
    setSuccess(true);
  };

  const showErrorMessage = (message, show) => {
    if (show) {
      setTimeout(() => appService.message({ flag: 'e', text: message, key: 'saveContext' }), 0);
    }
    setSuccess(false);
  };

  // -------------------------------------------------------------------------- //
  // get
  // -------------------------------------------------------------------------- //
  const get = useCallback(
    async (props = {}) => {
      // Use hookRef.current to get the latest values
      const hookValues = hookRef.current;

      let {
        _id = hookValues._id,
        condition = hookValues.condition,
        url = hookValues.url,
        params = hookValues.params,
        query = hookValues.query,
        fetchWithoutSubscription = hookValues.fetchWithoutSubscription,
        noFetchDataStore = hookValues.noFetchDataStore,
        removeSubscriptionIdFromHeader = hookValues.removeSubscriptionIdFromHeader,
        ignoreLoadingHide = hookValues.ignoreLoadingHide,
        ignoreLoadingShow = hookValues.ignoreLoadingShow,
        showLoading = true,
        headers = hookValues.headers,
        returnAsObject = hookValues.returnAsObject,
        hideLoading = false,
        paramKey = '_id',
        selectedTab = undefined,
      } = props;

      // Merge _id and condition
      if (_id) {
        if (typeof condition === 'object') {
          condition = { ...condition, [paramKey]: _id };
        } else {
          condition = { [paramKey]: _id };
        }

        // CRITICAL: If paramKey is not '_id', also add _id to condition
        // This ensures URL templates like /v1/accounts/:_id are properly replaced
        // even when using custom paramKeys like 'userId'
        if (paramKey !== '_id') {
          condition._id = _id;
        }
      }

      // Build URL with params and query
      const mergedQuery = {
        ...(getQuery?.() || {}),
        ...query,
      };

      // CROSS-PROFILE IMPORT: Check for import token in URL
      // If valid, remove entityProfile filter to allow loading entity from different profile
      const urlQuery = getQuery?.() || {};
      const importToken = getImportTokenFromUrl(new URLSearchParams(
        Object.entries(urlQuery).map(([k, v]) => [k, v]).join('&')
      )) || urlQuery[QUERY_PARAM_NAME];

      if (importToken) {
        const tokenData = parseImportToken(importToken);
        if (tokenData && tokenData.entityId === _id) {
          // Valid import token - remove entityProfile filter from query
          if (mergedQuery.filter) {
            try {
              const filterObj = typeof mergedQuery.filter === 'string'
                ? JSON.parse(mergedQuery.filter)
                : mergedQuery.filter;

              // Remove entityProfile from filter to allow cross-profile access
              if (filterObj.entityProfile) {
                delete filterObj.entityProfile;
                mergedQuery.filter = JSON.stringify(filterObj);
              }
            } catch (e) {
              console.warn('[useApiInitializer] Failed to parse filter for cross-profile import:', e);
            }
          }

          // Remove the import token from query (don't send to backend)
          delete mergedQuery[QUERY_PARAM_NAME];
        }
      }

      // FILTER BY COMPANY: Inject companyId in query if enabled
      // NOTE: The actual blocking of requests when company is not loaded is handled by:
      // - useApi.js (for document pages)
      // - ListProvider.jsx (for list pages)
      // This function just injects the companyId if available
      // SKIP if query already has 'q' parameter (encrypted query from ListProvider)
      // because companyId is already inside the encrypted 'q' payload
      const filterByCompany = storedCurrentPage?.settings?.filterByCompany;
      const hasEncryptedQuery = mergedQuery.q && typeof mergedQuery.q === 'string';
      if (filterByCompany && !hasEncryptedQuery) {
        const company = useZustand('auth', 'company', false);
        if (company?._id) {
          mergedQuery.companyId = company._id;
        }
        // If company is not loaded, the request will proceed without companyId
        // The calling code (useApi/ListProvider) should prevent this from happening
      }

      // CRITICAL: Pass endpoint from routing config to mergeUrlParamsQuery
      // This allows /:_id to be appended to the base URL (e.g., /v1/entities)
      // so the _id can be substituted into the URL path (e.g., /v1/entities/123)
      // BUT: If the API URL already contains param placeholders (like :_id?), don't append routing endpoint
      // because the URL is already complete and appending would duplicate the path
      const routingEndpoint = storedCurrentPage?.routing?.endpoint;
      // Don't append routing endpoint if:
      // 1. URL has optional params (:param?) - URL template is self-sufficient
      // 2. URL has NO params at all - URL is already fully resolved (custom URL passed)
      const urlHasAnyParams = url && /:\w+/.test(url);
      const urlHasOptionalParams = url && /:\w+\?/.test(url);
      const skipRoutingEndpoint = !urlHasAnyParams || urlHasOptionalParams;

      url = mergeUrlParamsQuery({
        url,
        endpoint: skipRoutingEndpoint ? null : routingEndpoint,
        params: { ...params, ...condition },
        query: mergedQuery,
      });

      const config = {
        url: url,
        returnAsObject,
        headers,
      };

      // Remove the x-subscriptionid header if requested
      if (removeSubscriptionIdFromHeader) {
        config.headers = { ...config.headers, 'x-subscriptionid': null };
      }

      let doAbort = false;

      try {
        // Set loading state to true before request (like post/put/del do)
        if (showLoading) {
          setLoading(true);
          setLoadingAreaStore({ visible: true });
        }
        // Mark that a manual refresh is in progress to prevent duplicate GET from useApi
        // Only mark if this is a manual refresh (showLoading=true) and NOT an auto-fetch (hideLoading=true)
        if (showLoading && !hideLoading) {
          markJustRefreshed();
        }
        incrementRequesting();

        // Validate _id before making the request
        if (_id && !isMongoObjectId(_id)) {
          // Mark this request as failed BEFORE showing notification/navigating
          // This prevents infinite retry loops when _id is invalid
          markRequestAsFailed(paramKey, _id, selectedTab);

          // Use the unified abortUnauthorizedDocument to handle invalid _id
          // This ensures proper GoBack with correct params and prevents multiple calls
          doAbort = true;
          return abortUnauthorizedDocument(navigate); // defaults to 'documentNotFound'
        }

        const [docs, result] = await axiosService.get(config);
        setError(null);

        // Check if request was successful before determining documentNotFound
        // IMPORTANT: If result is null/undefined or has no 'data' property, the request failed
        // We should NOT trigger documentNotFound in this case - only when API explicitly returns empty
        // Using 'data' in result checks if the property exists (even if null), not just its value
        const requestSuccessful = result != null && result?.success !== false && 'data' in result;

        // Check if a document was not found or unauthorized access (highSecurity)
        // Only check documentNotFound if the request was successful (API responded properly)
        const documentNotFound =
          requestSuccessful && _id && (!docs || (Array.isArray(docs) && docs.length === 0));

        // For highSecurity pages, verify user authorization
        // Use userId from URL params if available (e.g., /app/account/:userId/subscription/:_id)
        // Otherwise use _id (e.g., /app/account/:_id for MyProfile)
        const userIdToVerify = params?.userId;

        const unauthorizedAccess =
          storedCurrentPage?.highSecurity && userIdToVerify && userIdToVerify !== storedUser?._id;

        if (documentNotFound || unauthorizedAccess) {
          // Mark this request as failed BEFORE navigating away
          markRequestAsFailed(paramKey, _id, selectedTab);
          doAbort = true;
          return abortUnauthorizedDocument(navigate, {
            reason: unauthorizedAccess ? 'unauthorizedAccess' : 'documentNotFound',
          });
        }

        return handleResult(docs, result, noFetchDataStore);
      } catch (err) {
        // Don't show error message if request was intentionally aborted by API Tracker
        if (!err.isApiTrackerAbort) {
          setPageDataStore({ data: null, page: storedCurrentPage?.name });
          setError({ url: url, message: err.message || 'GET request failed' });

          // Mark this request as failed to prevent infinite retry loops
          markRequestAsFailed(paramKey, _id, selectedTab);
        }
      } finally {
        if (showLoading) {
          setLoadingAreaStore({ visible: false, extendedDelayToHide: 0 });
        }
        decrementRequesting();
        if (!doAbort) {
          done(hideLoading);
        }
        // ALWAYS set loading to false when hideLoading is true
        // This ensures loading state is reset regardless of doAbort status
        if (hideLoading) {
          setLoading(false);
        }
        setInitializing(false);
      }
    },
    [
      storedCurrentPage,
      setPageDataStore,
      setLoadingAreaStore,
      done,
      handleResult,
      navigate,
      params,
      setLoading,
    ]
  );

  // -------------------------------------------------------------------------- //
  // post
  // -------------------------------------------------------------------------- //
  const post = useCallback(
    async (props = {}) => {
      // Use hookRef.current to get the latest values
      const hookValues = hookRef.current;

      appService.notificationDestroy();

      let {
        dataSet,
        allValues = storedCurrentPage.data, // Original data with decorator applied (for request decorator)
        _id = hookValues._id,
        condition = hookValues.condition,
        url = hookValues.url,
        params = hookValues.params,
        query = hookValues.query,
        fetchWithoutSubscription = hookValues.fetchWithoutSubscription,
        noFetchDataStore = hookValues.noFetchDataStore,
        removeSubscriptionIdFromHeader = hookValues.removeSubscriptionIdFromHeader,
        ignoreLoadingHide = hookValues.ignoreLoadingHide,
        ignoreLoadingShow = hookValues.ignoreLoadingShow,
        showSuccessNotification = true,
        showErrorNotification = true,
        successMessage = 'data_created_success',
        failureMessage = 'data_created_failure',
        reload = true,
        headers = hookValues.headers,
        returnAsObject = hookValues.returnAsObject,
      } = props;

      // parse url for post
      const urlRaw = removeUrlParams(url);

      if (!urlRaw || typeof urlRaw !== 'string') {
        setError({ message: 'Parameter apiUrl is required and must be a string.' });
        return;
      }

      if (!ignoreLoadingShow) setLoading(true);
      setLoadingAreaStore({ visible: true, title: 'saving_data' });
      incrementRequesting();

      try {
        // Remove the x-subscriptionid header if requested
        if (removeSubscriptionIdFromHeader) {
          headers = { ...headers, 'x-subscriptionid': null };
        }

        const config = {
          url: urlRaw,
          data: dataSet,
          allValues, // Pass allValues for request decorator
          returnAsObject,
          headers,
        };

        const [docs, result] = await axiosService.post(config);

        // Mark that we just saved to prevent useApi from refetching
        markJustSaved();

        // Update URL with new document ID after successful creation
        // Use viewMode from store instead of checking URL (more secure)
        const storedViewMode = moduleStore().viewMode;
        if (storedViewMode === 'add') {
          routeService.updateUrlToId(docs?._id || docs?.[0]?._id);

          // Update viewMode from 'add' to 'edit' after successful save
          // This ensures CardHeader and EditButton update correctly
          moduleStore().setViewMode('edit');
        }

        // if (_id === 'add') {
        //   const newDocId = docs?._id || docs?.[0]?._id;
        //
        //   if (newDocId) {
        //     const currentQuery = getQuery();
        //     const currentEndpoint = getUrlEndpoint();
        //
        //     // Get paramKey from page config (e.g., 'subscriptionId', '_id')
        //     const pageParamKey = storedCurrentPage?.routing?.paramKey || '_id';
        //
        //     // Build schema URL from module config to extract current params
        //     const moduleUrl = storedModule?.url || '';
        //     const pageEndpoint = storedCurrentPage?.routing?.endpoint || '';
        //     const schemaUrl = moduleUrl + pageEndpoint;
        //
        //     // Extract current URL params (preserves userId, etc.)
        //     const currentParams = schemaUrl ? getParams(currentEndpoint, schemaUrl) : {};
        //
        //     // Replace 'add' with the new document ID using the correct paramKey
        //     currentParams[pageParamKey] = newDocId;
        //
        //     // Build the new URL with updated params
        //     const newUrl = mergeUrlParamsQuery({
        //       url: moduleUrl,
        //       endpoint: pageEndpoint,
        //       params: currentParams,
        //       query: currentQuery,
        //     });
        //
        //     // Update URL without navigation/reload
        //     updateUrl(newUrl, true);
        //   }
        // }

        if (reload) {
          await get({
            _id: docs?._id || docs?.[0]?._id,
          });
        }

        showSuccessMessage(successMessage, showSuccessNotification);

        // Update store with new document data (force=true to ensure it persists even in add mode)
        return handleResult(docs, result, noFetchDataStore, true);
      } catch (err) {
        // Don't show error message if request was intentionally aborted by API Tracker
        if (!err.isApiTrackerAbort) {
          setPageDataStore({ data: null, page: storedCurrentPage?.name });
          setError({ url: urlRaw, message: err.message || 'POST request failed' });

          showErrorMessage(failureMessage, showErrorNotification);
        }

        setLoadingAreaStore({ visible: false });
        throw err;
      } finally {
        if (!ignoreLoadingHide) setLoading(false);
        decrementRequesting();
      }
    },
    [
      storedFormDebug,
      storedCurrentPage,
      storedModule,
      setPageDataStore,
      setLoadingAreaStore,
      handleResult,
      showSuccessMessage,
      showErrorMessage,
      get,
    ]
  );

  // -------------------------------------------------------------------------- //
  // put
  // -------------------------------------------------------------------------- //
  const put = useCallback(
    async (props = {}) => {
      // Use hookRef.current to get the latest values
      const hookValues = hookRef.current;

      appService.notificationDestroy();

      let {
        dataSet = {},
        allValues = storedCurrentPage.data, // Original data with decorator applied (for request decorator)
        _id = hookValues._id,
        condition = hookValues.condition,
        url = hookValues.url,
        params = hookValues.params,
        query = hookValues.query,
        fetchWithoutSubscription = hookValues.fetchWithoutSubscription,
        noFetchDataStore = hookValues.noFetchDataStore,
        removeSubscriptionIdFromHeader = hookValues.removeSubscriptionIdFromHeader,
        ignoreLoadingHide = hookValues.ignoreLoadingHide,
        ignoreLoadingShow = hookValues.ignoreLoadingShow,
        showSuccessNotification = true,
        showErrorNotification = true,
        successMessage = 'data_updated_success',
        failureMessage = 'data_updated_failure',
        headers = hookValues.headers,
        returnAsObject = hookValues.returnAsObject,
      } = props;

      // Merge _id and condition
      if (_id) {
        if (typeof condition === 'object') {
          condition = { ...condition, _id: _id };
        } else {
          condition = { _id };
        }
      }

      // Build URL with params and query
      // Pass endpoint from routing config to allow :_id substitution
      // BUT: If the API URL already contains param placeholders (like :_id?), don't append routing endpoint
      const routingEndpoint = storedCurrentPage?.routing?.endpoint;
      // Don't append routing endpoint if:
      // 1. URL has optional params (:param?) - URL template is self-sufficient
      // 2. URL has NO params at all - URL is already fully resolved (custom URL passed)
      const urlHasAnyParams = url && /:\w+/.test(url);
      const urlHasOptionalParams = url && /:\w+\?/.test(url);
      const skipRoutingEndpoint = !urlHasAnyParams || urlHasOptionalParams;

      url = mergeUrlParamsQuery({
        url,
        endpoint: skipRoutingEndpoint ? null : routingEndpoint,
        params: { ...params, ...condition },
      });

      if (!url || typeof url !== 'string') {
        setError({ message: 'Parameter apiUrl is required and must be a string.' });
        return;
      }

      if (!ignoreLoadingShow) setLoading(true);
      setLoadingAreaStore({ visible: true, title: 'updating_data' });
      incrementRequesting();

      try {
        // Remove the x-subscriptionid header if requested
        if (removeSubscriptionIdFromHeader) {
          headers = { ...headers, 'x-subscriptionid': null };
        }

        const axiosConfig = {
          url: url,
          data: dataSet,
          allValues, // Pass allValues for request decorator
          returnAsObject,
          headers,
        };

        const [docs, result] = await axiosService.put(axiosConfig);

        // Mark that we just saved to prevent useApi from refetching
        markJustSaved();

        showSuccessMessage(successMessage, showSuccessNotification);

        // Merge with existing data to preserve fields not returned by PUT
        return handleResult(docs, result, noFetchDataStore, true);
      } catch (err) {
        // Don't show error message if request was intentionally aborted by API Tracker
        if (!err.isApiTrackerAbort) {
          // setPageDataStore({ data: null, page: storedCurrentPage?.name });
          setError({ url: url, message: err.message || 'PUT request failed' });

          showErrorMessage(failureMessage, showErrorNotification);
        }

        throw err;
      } finally {
        if (!ignoreLoadingHide) setLoading(false);
        setLoadingAreaStore({ visible: false, extendedDelayToHide: 400 });
        decrementRequesting();
      }
    },
    [
      storedCurrentPage,
      setPageDataStore,
      setLoadingAreaStore,
      handleResult,
      showSuccessMessage,
      showErrorMessage,
    ]
  );

  // -------------------------------------------------------------------------- //
  // del
  // -------------------------------------------------------------------------- //
  const del = useCallback(
    async (props = {}) => {
      // Use hookRef.current to get the latest values
      const hookValues = hookRef.current;

      appService.notificationDestroy();

      let {
        _id = hookValues._id,
        condition = hookValues.condition,
        url = hookValues.url,
        params = hookValues.params,
        query = hookValues.query,
        fetchWithoutSubscription = hookValues.fetchWithoutSubscription,
        noFetchDataStore = hookValues.noFetchDataStore,
        removeSubscriptionIdFromHeader = hookValues.removeSubscriptionIdFromHeader,
        ignoreLoadingHide = hookValues.ignoreLoadingHide,
        ignoreLoadingShow = hookValues.ignoreLoadingShow,
        showSuccessNotification = true,
        showErrorNotification = true,
        successMessage = 'data_removed_success',
        failureMessage = 'data_removed_failure',
        headers = hookValues.headers,
        returnAsObject = hookValues.returnAsObject,
      } = props;

      // Merge _id and condition
      if (_id) {
        if (typeof condition === 'object') {
          condition = { ...condition, _id: _id };
        } else {
          condition = { _id };
        }
      }

      // Build URL with params and query
      // Pass endpoint from routing config to allow :_id substitution
      // BUT: If the API URL already contains param placeholders (like :_id?), don't append routing endpoint
      const routingEndpoint = storedCurrentPage?.routing?.endpoint;
      // Don't append routing endpoint if:
      // 1. URL has optional params (:param?) - URL template is self-sufficient
      // 2. URL has NO params at all - URL is already fully resolved (custom URL passed)
      const urlHasAnyParams = url && /:\w+/.test(url);
      const urlHasOptionalParams = url && /:\w+\?/.test(url);
      const skipRoutingEndpoint = !urlHasAnyParams || urlHasOptionalParams;

      url = mergeUrlParamsQuery({
        url,
        endpoint: skipRoutingEndpoint ? null : routingEndpoint,
        params: { ...params, ...condition },
      });

      if (!url || typeof url !== 'string') {
        setError({ message: 'Parameter apiUrl is required and must be a string.' });
        return;
      }

      if (!ignoreLoadingShow) setLoading(true);
      setLoadingAreaStore({ visible: true, title: 'removing_document' });
      incrementRequesting();

      try {
        // Remove x-subscriptionid header if requested
        if (removeSubscriptionIdFromHeader) {
          headers = { ...headers, 'x-subscriptionid': null };
        }

        const [docs, result] = await axiosService.delete({
          url: url,
          _id: condition,
          returnAsObject,
          headers,
        });

        showSuccessMessage(successMessage, showSuccessNotification);

        return handleResult(docs, result, noFetchDataStore);
      } catch (err) {
        // Don't show error message if request was intentionally aborted by API Tracker
        if (!err.isApiTrackerAbort) {
          setPageDataStore({ data: null, page: storedCurrentPage?.name });
          setError({ url: url, message: err.message || 'DELETE request failed' });

          showErrorMessage(failureMessage, showErrorNotification);
        }

        throw err;
      } finally {
        if (!ignoreLoadingHide) setLoading(false);
        setLoadingAreaStore({ visible: false, extendedDelayToHide: 400 });
        decrementRequesting();
      }
    },
    [
      storedCurrentPage,
      setPageDataStore,
      setLoadingAreaStore,
      handleResult,
      showSuccessMessage,
      showErrorMessage,
    ]
  );

  // Ref to track if we're in "new document" mode (new or duplicate)
  // This prevents stale _id references from causing PUT instead of POST
  const isNewDocumentModeRef = useRef(false);

  // -------------------------------------------------------------------------- //
  // upsert
  // -------------------------------------------------------------------------- //
  const upsert = useCallback(
    async (opts = {}) => {
      const { dataSet, _id: optsId } = opts || {};

      // Check _id from multiple sources: opts._id, dataSet._id
      // But IGNORE if we're in "new document" mode (resetForNewDocument was called)
      const _id = isNewDocumentModeRef.current ? null : optsId || dataSet?._id;

      // Reset the flag after using it (single use per new/duplicate action)
      if (isNewDocumentModeRef.current) {
        isNewDocumentModeRef.current = false;
      }

      // Remove _id from dataSet body in BOTH branches: routing already carries it via
      // _id/optsId. A stale _id left in dataSet (e.g. from a duplicated document's
      // captured form state) would otherwise ride into the POST body and collide with
      // the original document's _id (E11000 duplicate key on insert).
      if (dataSet?._id) {
        delete dataSet._id;
      }

      if (_id) {
        return put({
          ...opts,
          _id,
          dataSet,
        });
      } else {
        return post(opts);
      }
    },
    [put, post]
  );

  // -------------------------------------------------------------------------- //
  // resetForNewDocument
  // Clears internal _id state for duplicate/new document operations
  // This ensures upsert will do POST instead of PUT
  // -------------------------------------------------------------------------- //
  const resetForNewDocument = useCallback(() => {
    // Set flag to force POST on next upsert call
    isNewDocumentModeRef.current = true;
    // Clear internal _id in hookRef
    hookRef.current._id = null;
    // Clear documentId state
    setDocumentId(null);
    // Clear data state
    setData(null);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. COMPUTED VALUES
  // ─────────────────────────────────────────────────────────────────────────────
  const urlInfo = useMemo(() => {
    const fullUrl = getUrl(); // Full URL: https://example.com/app/account/123?tab=record
    const domain = getDomain(); // Domain: https://example.com
    const endpoint = getUrlEndpoint(); // Endpoint: /app/account/123
    const query = getQuery(); // Query object: { tab: 'record' }

    // Build schema URL from module config
    const moduleUrl = storedModule?.url || '';
    const pageEndpoint = storedCurrentPage?.routing?.endpoint || '';
    const api = storedCurrentPage?.api?.url;
    const schemaUrl = moduleUrl + pageEndpoint; // Ex: /app/account/:_id

    // Extract params from current URL using schema
    const params = schemaUrl ? getParams(endpoint, schemaUrl) : {};

    // Build full URL with module base + endpoint
    const moduleFullUrl = domain + moduleUrl + pageEndpoint;

    return {
      full: fullUrl, // Full browser URL with domain + pathname + query
      domain, // Domain only: https://example.com
      endpoint, // Pathname only: /app/account/123
      moduleUrl, // Module base URL: /app/account
      pageEndpoint, // Page endpoint template: /:_id
      schemaUrl, // Full schema: /app/account/:_id
      moduleFullUrl, // Module base + page endpoint: https://example.com/app/account/:_id
      api,
      params, // Extracted params: { _id: '123' }
      query, // Query object: { tab: 'record' }
    };
  }, [storedModule, storedCurrentPage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. BUILD API OBJECT (used for both return and global store)
  // ─────────────────────────────────────────────────────────────────────────────
  const api = useMemo(
    () => ({
      _id: hookRef.current._id,
      documentId,
      get,
      refresh: get,
      put,
      post,
      del,
      done,
      upsert,
      resetForNewDocument,
      setData,
      data,
      loading,
      setLoading,
      error,
      success,
      summary,
      initializing,
      setInitializing,
      urlInfo,
    }),
    [
      documentId,
      get,
      put,
      post,
      del,
      done,
      upsert,
      resetForNewDocument,
      setData,
      data,
      loading,
      setLoading,
      error,
      success,
      summary,
      initializing,
      setInitializing,
      urlInfo,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 10. EFFECTS II - Register API in global store
  // ─────────────────────────────────────────────────────────────────────────────
  // Use ref to store the latest function references for stable wrappers
  const apiRef = useRef({});

  // Keep ref updated synchronously on every render
  apiRef.current = {
    get,
    post,
    put,
    del,
    upsert,
    done,
    urlInfo,
    resetForNewDocument,
    setLoading,
    loading,
    data,
    error,
    success,
    summary,
  };

  // Track if API has been registered to avoid cleanup race conditions
  const apiRegisteredRef = useRef(false);

  useEffect(() => {
    if (!get || !post || !put || !del || !upsert || !done) return;

    // Skip if already registered (prevents cleanup/re-register race condition)
    if (apiRegisteredRef.current) return;

    const setStateGlobal = useZustand('global', 'setState', false);

    // Create stable wrapper functions that always call the latest version via ref
    // This prevents infinite loops when refresh() is called because the wrapper
    // references are stable (created once) but always use the latest functions via ref
    const stableApi = {
      get: (...args) => apiRef.current.get?.(...args),
      refresh: (...args) => apiRef.current.get?.(...args),
      post: (...args) => apiRef.current.post?.(...args),
      put: (...args) => apiRef.current.put?.(...args),
      del: (...args) => apiRef.current.del?.(...args),
      upsert: (...args) => apiRef.current.upsert?.(...args),
      done: (...args) => apiRef.current.done?.(...args),
      resetForNewDocument: (...args) => apiRef.current.resetForNewDocument?.(...args),
      setLoading: (...args) => apiRef.current.setLoading?.(...args),
      // Use getters for reactive values to always return the current value from ref
      get urlInfo() {
        return apiRef.current.urlInfo;
      },
      get loading() {
        return apiRef.current.loading;
      },
      get data() {
        return apiRef.current.data;
      },
      get error() {
        return apiRef.current.error;
      },
      get success() {
        return apiRef.current.success;
      },
      get summary() {
        return apiRef.current.summary;
      },
    };

    setStateGlobal('useApi', stableApi);
    apiRegisteredRef.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!get, !!post, !!put, !!del, !!upsert, !!done]);

  // Separate cleanup effect that only runs on unmount
  useEffect(() => {
    return () => {
      const setStateGlobal = useZustand('global', 'setState', false);
      setStateGlobal('useApi');
      apiRegisteredRef.current = false;
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 11. RETURN
  // ─────────────────────────────────────────────────────────────────────────────
  return api;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default useApiInitializer;
